/**
 * The Chief of Staff — ask anything about the briefing on screen.
 *
 * Grounded in the briefing and the module analyst pack behind it, so an answer
 * either cites the pack or says which module would have carried it.
 */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Send, X, Sparkles } from 'lucide-react';

const STARTERS = [
  'What single thing is most likely to blow the completion date?',
  'Where is the money leaking, and how much of it?',
  'What should I not spend the review discussing?',
];

export function ChiefOfStaffChat({ projectId, open, onClose }) {
  const [sessionId] = useState(() => `cos-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const bottom = useRef(null);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);
  useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    const t0 = Date.now();
    const t = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(t);
  }, [busy]);

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || busy) return;
    setQuestion('');
    setMessages(m => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/ai-analyst/ceo-briefing/chat`,
        { question: q, sessionId }, { timeout: 180000 });
      setMessages(data?.messages || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The Chief of Staff could not answer that');
      setMessages(m => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="cos-chat-overlay"
         onClick={onClose}>
      <div className="w-full max-w-xl h-full bg-background border-l border-border flex flex-col"
           onClick={e => e.stopPropagation()} data-testid="cos-chat-panel">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Chief of Staff</p>
            <p className="text-[11px] text-muted-foreground">
              Answers against this briefing and the analyst pack behind it
            </p>
          </div>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-muted"
                  data-testid="cos-chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && !busy && (
            <div className="space-y-2" data-testid="cos-chat-starters">
              <p className="text-xs text-muted-foreground">Try one of these:</p>
              {STARTERS.map((s, i) => (
                <button key={i} onClick={() => ask(s)} data-testid={`cos-chat-starter-${i}`}
                        className="block w-full text-left text-xs rounded-lg border border-border
                                   px-3 py-2 hover:border-amber-500/60 hover:bg-muted/40 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'text-right' : ''}
                 data-testid={`cos-chat-msg-${i}`}>
              <div className={`inline-block max-w-[85%] text-xs leading-relaxed rounded-xl px-3 py-2
                               whitespace-pre-wrap text-left ${m.role === 'user'
                                 ? 'bg-amber-500/15 text-foreground'
                                 : 'bg-muted/50 text-muted-foreground'}`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground"
                 data-testid="cos-chat-busy">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500" />
              reading the pack… <span className="font-mono">{elapsed}s</span>
            </div>
          )}
          <div ref={bottom} />
        </div>

        <div className="border-t border-border p-3 flex gap-2">
          <input value={question} onChange={e => setQuestion(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                 placeholder="Ask about any figure, risk or decision in this briefing"
                 data-testid="cos-chat-input"
                 className="flex-1 text-xs bg-muted/40 border border-border rounded-lg px-3 py-2
                            outline-none focus:border-amber-500/60" />
          <button onClick={() => ask()} disabled={busy || !question.trim()}
                  data-testid="cos-chat-send"
                  className="px-3 rounded-lg bg-amber-500 text-black disabled:opacity-40">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChiefOfStaffButton({ onClick }) {
  return (
    <button onClick={onClick} data-testid="cos-chat-open"
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-border
                       hover:border-amber-500/60 hover:bg-muted/40 transition-colors">
      <MessageSquare className="w-3.5 h-3.5" /> Ask the Chief of Staff
    </button>
  );
}

export default ChiefOfStaffChat;
